// OR-6455: internal-viewer read-only chat panel.
//
// Renders a chat the caller doesn't own, via a one-shot snapshot fetch —
// never a live socket subscribe. Structurally read-only for MUTATIONS:
// EpiChatPanelLayout's `readOnly` prop hides the composer and disables every
// transcript WRITE affordance (clarifying-question / checkpoint answers),
// and buildReadOnlyChatPage's `page` has no reachable action that does
// anything but return. Asset-card PREVIEW navigation is intentionally NOT
// disabled — clicking an artifact/resource card routes to that asset's
// detail page via the same pure navigation handlers the live panel uses
// (see useChatResourceNavigation); it's a read, not an action on the chat.
//
// The chat switcher (ActiveChatHeader) is the real, fully-functional one —
// never disabled or omitted — so a viewer can always switch OUT to another
// chat without a dead end.

import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { EpiChatPanelLayout } from '@verantos-dev/feature-epi-chat';

import { ActiveChatHeader } from './ActiveChatHeader';
import { ChatPanelTitle } from './ChatPanelTitle';
import { getOwnerAvatarInitials } from '../emitters/ownerAvatarInitials';
import { useChatResourceNavigation } from '../hooks/useChatResourceNavigation';
import { useInternalChatSnapshotQuery } from '../lib/api/useProjectChats';
import { buildReadOnlyChatPage } from '../lib/readOnlyChat/buildReadOnlyChatPage';

interface ReadOnlyChatPanelProps {
  projectId: string;
  chatId: string;
  ownerUserId: string | undefined;
  /** Best-effort Auth0-resolved label ("Given Family", Auth0 name, or
   * email); preferred over `ownerUserId` whenever present. */
  ownerDisplayName: string | undefined;
}

const formatFetchedAt = (iso: string): string => {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(
    new Date(ts)
  );
};

export const ReadOnlyChatPanel = ({
  projectId,
  chatId,
  ownerUserId,
  ownerDisplayName,
}: ReadOnlyChatPanelProps) => {
  const { data, isLoading, isError, isFetching, refetch } = useInternalChatSnapshotQuery({
    chatId,
    enabled: Boolean(chatId),
  });
  const { onArtifactOpen, onAvSlugClick, navigate, onConceptGroupNavigate } =
    useChatResourceNavigation(projectId);

  const page = useMemo(() => buildReadOnlyChatPage(chatId, data?.snapshot ?? null), [chatId, data]);

  const ownerLabel = ownerDisplayName ?? ownerUserId;
  const bannerLabel = ownerLabel
    ? `Viewing ${ownerLabel}'s chat. Only they can send messages here.`
    : undefined;
  // The owner's, not the viewer's — MessageBubble's avatar otherwise
  // defaults to the logged-in viewer's own initials via useAuth(), which
  // is wrong here: every "user" turn in this snapshot was sent by the
  // chat's owner, not whoever is currently looking at it.
  const ownerAvatarInitials = getOwnerAvatarInitials(ownerLabel);

  const refreshControl = (
    <Stack direction="row" alignItems="center" spacing={1}>
      {data && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="read-only-chat-fetched-at"
        >
          As of {formatFetchedAt(data.fetchedAt)}
        </Typography>
      )}
      <Tooltip title="Refresh to view new messages">
        <span>
          <IconButton
            size="small"
            onClick={() => void refetch()}
            disabled={isFetching}
            data-testid="read-only-chat-refresh"
            aria-label="Refresh snapshot"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <ChatPanelTitle trailingContent={refreshControl} />
      {/* OR-6455: the real switcher — a viewer must always be able to
          switch to another chat, never disabled or hidden here. */}
      <ActiveChatHeader projectId={projectId} chatId={chatId} />
      {/* Unmissable at first glance, not just the composer-replacement text
          at the bottom (EpiChatPanelLayout's readOnlyBannerLabel, kept too
          as reinforcement) — a viewer who never scrolls down must still
          immediately know this isn't their own, writable chat. Body text
          intentionally omitted: bannerLabel below already spells out whose
          chat this is and that only they can send messages — one place for
          that sentence, not two (Jira OR-6455 comment #81601, item 1). */}
      <Alert
        severity="warning"
        icon={<VisibilityIcon fontSize="inherit" />}
        // MUI's Alert defaults to alignItems: 'flex-start' — built for
        // multi-line messages, where the icon should sit at the top. This
        // banner is a single short line now, so flex-start left the icon
        // and text visibly offset; 'center' lines them up.
        sx={{ borderRadius: 0, flexShrink: 0, alignItems: 'center' }}
        data-testid="read-only-chat-top-banner"
      >
        {/* body2 matches chat message text size (MessageBubble's body
            Typography) — subtitle2 (matching the chat-name trigger above)
            read too close to a section title; bold instead distinguishes
            it as a status label without sizing it up. */}
        <AlertTitle variant="body2" sx={{ marginBottom: 0, fontWeight: 600 }}>
          Read only
        </AlertTitle>
      </Alert>
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', padding: 4 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : isError ? (
          <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', padding: 4 }}>
            <Typography variant="body2" color="error">
              Couldn’t load this chat.
            </Typography>
          </Stack>
        ) : (
          <EpiChatPanelLayout
            page={page}
            readOnly
            readOnlyBannerLabel={bannerLabel}
            userAvatarInitials={ownerAvatarInitials}
            onArtifactOpen={onArtifactOpen}
            onAvSlugClick={onAvSlugClick}
            onResourceNavigate={navigate}
            onConceptGroupNavigate={onConceptGroupNavigate}
            data-testid="read-only-chat-panel"
          />
        )}
      </Box>
    </Box>
  );
};

export default ReadOnlyChatPanel;
